import { z } from 'zod';

/**
 * Canonical machine-readable error codes returned in the API error envelope.
 * Keep these stable — clients branch on them.
 */
export const ERROR_CODES = [
  // generic
  'BAD_REQUEST',
  'VALIDATION_ERROR',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'IDEMPOTENCY_KEY_REQUIRED',
  'IDEMPOTENCY_KEY_CONFLICT',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'INTERNAL_ERROR',
  'SERVICE_UNAVAILABLE',
  'UPSTREAM_ERROR',
  'FEATURE_DISABLED',
  // auth
  'INVALID_CREDENTIALS',
  'ACCOUNT_LOCKED',
  /** Admin accounts must sign in through /auth/admin/login/* (password + SMS code). */
  'ADMIN_2FA_REQUIRED',
  'ACCOUNT_DISABLED',
  'EMAIL_NOT_VERIFIED',
  'PHONE_NOT_VERIFIED',
  'OTP_INVALID',
  'OTP_EXPIRED',
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
  'TOKEN_REVOKED',
  'REFRESH_REUSE_DETECTED',
  // domain
  'VENDOR_NOT_APPROVED',
  'PRODUCT_NOT_AVAILABLE',
  'OUT_OF_STOCK',
  'CART_EMPTY',
  'ADDRESS_NOT_SERVICEABLE',
  'COD_NOT_ELIGIBLE',
  'PAYMENT_FAILED',
  'PAYMENT_AMOUNT_MISMATCH',
  'PAYMENT_SIGNATURE_INVALID',
  'PAYMENT_WEBHOOK_UNCONFIGURED',
  'ORDER_NOT_CANCELLABLE',
  'PACKAGE_NOT_CANCELLABLE',
  'RETURN_WINDOW_CLOSED',
  'RETURN_NOT_REJECTABLE',
  'RETURN_NOT_IN_QC',
  'RETURN_NOT_DISPUTED',
  'RETURN_ALREADY_COLLECTED',
  'INVOICE_IMMUTABLE',
  'INSUFFICIENT_WALLET_BALANCE',
] as const;

export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

/** Map an error code to a sane default HTTP status. The backend may override per case. */
export const DEFAULT_HTTP_STATUS: Record<ErrorCode, number> = {
  BAD_REQUEST: 400,
  VALIDATION_ERROR: 422,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  RATE_LIMITED: 429,
  IDEMPOTENCY_KEY_REQUIRED: 400,
  IDEMPOTENCY_KEY_CONFLICT: 409,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
  // 424, not 502: the proxy in front of the API (Easypanel) swaps any 502 for
  // its own HTML page without CORS headers, so the browser saw "could not
  // reach the server" instead of the provider's reason ("Shiprocket: …").
  UPSTREAM_ERROR: 424,
  FEATURE_DISABLED: 403,
  INVALID_CREDENTIALS: 401,
  ACCOUNT_LOCKED: 423,
  ADMIN_2FA_REQUIRED: 401,
  ACCOUNT_DISABLED: 403,
  EMAIL_NOT_VERIFIED: 403,
  PHONE_NOT_VERIFIED: 403,
  OTP_INVALID: 400,
  OTP_EXPIRED: 400,
  TOKEN_EXPIRED: 401,
  TOKEN_INVALID: 401,
  TOKEN_REVOKED: 401,
  REFRESH_REUSE_DETECTED: 401,
  VENDOR_NOT_APPROVED: 403,
  PRODUCT_NOT_AVAILABLE: 409,
  OUT_OF_STOCK: 409,
  CART_EMPTY: 409,
  ADDRESS_NOT_SERVICEABLE: 422,
  COD_NOT_ELIGIBLE: 422,
  PAYMENT_FAILED: 402,
  PAYMENT_AMOUNT_MISMATCH: 409,
  PAYMENT_SIGNATURE_INVALID: 400,
  PAYMENT_WEBHOOK_UNCONFIGURED: 503,
  ORDER_NOT_CANCELLABLE: 409,
  PACKAGE_NOT_CANCELLABLE: 409,
  RETURN_WINDOW_CLOSED: 409,
  RETURN_NOT_REJECTABLE: 409,
  RETURN_NOT_IN_QC: 409,
  RETURN_NOT_DISPUTED: 409,
  RETURN_ALREADY_COLLECTED: 409,
  INVOICE_IMMUTABLE: 409,
  INSUFFICIENT_WALLET_BALANCE: 409,
};
